import type React from 'react'
import type {
  InformationNeed,
  QuantitativeResponse,
  WidgetConfig,
} from '@knownow/domain'

export interface QuantitativeResponseDisplayProps {
  readonly need: InformationNeed
  readonly response: QuantitativeResponse
}

/**
 * Per-respondent structured value. Intentionally NOT a chart — inside a
 * single conversation a single data point is clearer as a plain readout
 * (e.g. "You rated 4 / 5"). Plan-level aggregations render charts separately.
 */
export function QuantitativeResponseDisplay({
  need,
  response,
}: QuantitativeResponseDisplayProps): React.ReactElement | null {
  const config = need.widgetConfig
  if (config === null) return null

  switch (config.type) {
    case 'rating':
      return <RatingValue response={response} config={config} />
    case 'nps':
      return <NpsValue response={response} />
    case 'single_choice':
    case 'multiple_choice':
      return <ChoiceValue response={response} />
    case 'ranking':
      return <RankingValue response={response} config={config} />
    default:
      return assertUnreachable(config)
  }
}

function RatingValue({
  response,
  config,
}: {
  response: QuantitativeResponse
  config: Extract<WidgetConfig, { type: 'rating' }>
}): React.ReactElement {
  const value = response.numericValue ?? 0
  const max = config.max
  return (
    <div className="flex items-center gap-3" data-testid="rating-value">
      <span className="font-serif text-3xl text-neutral-900">
        {value}
        <span className="text-neutral-400 text-lg"> / {max}</span>
      </span>
      {config.labels && (
        <span className="text-xs text-neutral-500">
          {config.labels.low} → {config.labels.high}
        </span>
      )}
    </div>
  )
}

function NpsValue({
  response,
}: {
  response: QuantitativeResponse
}): React.ReactElement {
  const score = response.numericValue ?? 0
  const bucket =
    score >= 9 ? 'Promoter' : score >= 7 ? 'Passive' : 'Detractor'
  const variantCls =
    bucket === 'Promoter'
      ? 'bg-success-light text-success-dark'
      : bucket === 'Passive'
        ? 'bg-warning-light text-warning-dark'
        : 'bg-error-light text-error-dark'
  return (
    <div className="flex items-center gap-3" data-testid="nps-value">
      <span className="font-serif text-3xl text-neutral-900">
        {score}
        <span className="text-neutral-400 text-lg"> / 10</span>
      </span>
      <span
        className={[
          'inline-flex items-center px-2 py-0.5 text-xs rounded-full font-medium',
          variantCls,
        ].join(' ')}
      >
        {bucket}
      </span>
    </div>
  )
}

function ChoiceValue({
  response,
}: {
  response: QuantitativeResponse
}): React.ReactElement {
  const options = response.selectedOptions ?? []
  if (options.length === 0) {
    return (
      <p className="text-sm text-neutral-400" data-testid="choice-empty">
        (no selection)
      </p>
    )
  }
  return (
    <ul className="flex flex-wrap gap-2" data-testid="choice-value">
      {options.map((option) => (
        <li
          key={option}
          className="rounded-full bg-brand-100 text-brand-700 px-3 py-1 text-sm"
        >
          {option}
        </li>
      ))}
    </ul>
  )
}

function RankingValue({
  response,
  config,
}: {
  response: QuantitativeResponse
  config: Extract<WidgetConfig, { type: 'ranking' }>
}): React.ReactElement {
  const ordered = response.orderedItems ?? config.items
  return (
    <ol className="space-y-1" data-testid="ranking-value">
      {ordered.map((item, i) => (
        <li key={item} className="text-sm text-neutral-800">
          <span className="font-serif text-neutral-500 me-2">{i + 1}.</span>
          {item}
        </li>
      ))}
    </ol>
  )
}

function assertUnreachable(value: WidgetConfig): never {
  throw new Error(`Unhandled widget config: ${JSON.stringify(value)}`)
}
